/**
 * Shipping Params Builder
 *
 * Builds unified line_items format for shipping rate requests.
 * Handles logged-in users, guest checkout, single-address, and multi-address.
 */

/**
 * Check if an address is a guest address (not stored in database)
 * @param {object} address
 * @returns {boolean}
 */
export const isGuestAddress = address => {
  return address?.id && String(address.id).startsWith('guest-')
}

/**
 * Format address object for guest checkout API request
 * @param {object} address
 * @returns {object}
 */
export const formatGuestAddress = address => ({
  address_line_1: address.address_line_1,
  address_line_2: address.address_line_2 || null,
  city: address.city,
  state: address.state,
  zip: address.zip,
  country_code: address.country_code,
  full_name: address.full_name,
  company_name: address.company_name || null,
  address_phone: address.address_phone || null
})

/**
 * Build a single line_item for the request
 * @param {number} cartItemId
 * @param {number} quantity
 * @param {object} address
 * @returns {object}
 */
export const buildLineItem = (cartItemId, quantity, address) => {
  const lineItem = {
    cart_item_id: cartItemId,
    quantity
  }

  if (isGuestAddress(address)) {
    lineItem.guest_address = formatGuestAddress(address)
  } else {
    lineItem.customer_address_id = address.id
  }

  return lineItem
}

/**
 * Build line_items for single-address shipping (all items to one address)
 * @param {object} options
 * @param {object} options.address - Selected customer address
 * @param {Array} options.cartItems - Cart items to ship
 * @returns {object|null}
 */
export const buildSingleAddressParams = ({ address, cartItems }) => {
  if (!address?.id || !cartItems?.length) return null

  const line_items = cartItems.map(item =>
    buildLineItem(item.cart_item_id, item.quantity, address)
  )

  return { line_items }
}

/**
 * Build line_items for multi-address shipping
 * @param {object} options
 * @param {Array} options.multiAddressSelection - Array of { cartItemId, address, quantity }
 * @returns {object|null}
 */
export const buildMultiAddressParams = ({ multiAddressSelection }) => {
  if (!multiAddressSelection?.length) return null

  const line_items = multiAddressSelection.map(selection =>
    buildLineItem(selection.cartItemId, selection.quantity.value, selection.address)
  )

  return { line_items }
}

/**
 * Build shipping params based on checkout configuration
 * @param {object} options
 * @param {object} options.selectedAddress - Selected customer address
 * @param {Array} options.cartItems - Cart items (shippable only)
 * @param {Array} options.multiAddressSelection - Multi-address selections
 * @param {boolean} options.isMultiAddressEnabled - Whether multi-address shipping is enabled
 * @returns {object|null}
 */
export const buildShippingParams = ({
  selectedAddress,
  cartItems,
  multiAddressSelection,
  isMultiAddressEnabled
}) => {
  // Multi-address shipping
  if (isMultiAddressEnabled && multiAddressSelection?.length > 0) {
    return buildMultiAddressParams({ multiAddressSelection })
  }

  // Single-address shipping
  return buildSingleAddressParams({ address: selectedAddress, cartItems })
}
